import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Table, Pagination, Nav } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set, push, remove, get } from 'firebase/database';
import { Link, useLocation } from 'react-router-dom';

interface BaseCar {
  name: string;
  brand?: string;
  model?: string;
  year?: string;
  is_active?: boolean;
}

interface Car extends BaseCar {
  plate?: string;
}

interface CarWithId extends BaseCar {
  id: string;
  plate: string;
  is_active: boolean;
}

interface EditableCar extends CarWithId {
  isEditing: boolean;
}

const MoschtCars = () => {
  const { currentUser } = useAuth();
  const [cars, setCars] = useState<Record<string, Car>>({});
  const [carPlates, setCarPlates] = useState<Record<string, string>>({});
  const [ownedCarIds, setOwnedCarIds] = useState<string[]>([]);
  const [editingCars, setEditingCars] = useState<Record<string, EditableCar>>({});
  const [newCar, setNewCar] = useState({
    name: '',
    brand: '',
    model: '',
    year: '',
    licensePlate: ''
  });
  const [showIds, setShowIds] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const location = useLocation();

  useEffect(() => {
    if (!currentUser) return;

    const carsRef = ref(database, 'cars');
    const carsUnsubscribe = onValue(carsRef, (snapshot) => {
      const data = snapshot.val();
      setCars(data || {});

      // Initialize editing state for all cars
      const editableData: Record<string, EditableCar> = {};
      Object.entries(data || {}).forEach(([id, carData]) => {
        const car = carData as Car;
        editableData[id] = {
          ...(car as Car),
          id,
          plate: '',
          is_active: car.is_active ?? true,
          isEditing: false
        };
      });
      setEditingCars(editableData);

      // Fetch plates for all cars
      const carIds = Object.keys(data || {});
      const plateUnsubscribes = carIds.map(carId => {
        const plateRef = ref(database, `cars/${carId}/plate`);
        return onValue(plateRef, (plateSnapshot) => {
          setCarPlates(prev => ({
            ...prev,
            [carId]: plateSnapshot.val() || ''
          }));
          // Update plate in editing state
          setEditingCars(prev => ({
            ...prev,
            [carId]: {
              ...prev[carId],
              plate: plateSnapshot.val() || ''
            }
          }));
        });
      });

      return () => plateUnsubscribes.forEach(unsubscribe => unsubscribe());
    });

    const ownedCarsRef = ref(database, `user_settings/${currentUser.uid}/ownedCars`);
    const ownedCarsUnsubscribe = onValue(ownedCarsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setOwnedCarIds(Object.values(data));
    });

    return () => {
      carsUnsubscribe();
      ownedCarsUnsubscribe();
    };
  }, [currentUser]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!currentUser) return;

    try {
      // Create new car
      const carsRef = ref(database, 'cars');
      const newCarRef = await push(carsRef, {
        name: newCar.name,
        brand: newCar.brand,
        model: newCar.model,
        year: newCar.year,
        is_active: true
      });

      // Set license plate separately
      if (newCar.licensePlate) {
        const plateRef = ref(database, `cars/${newCarRef.key}/plate`);
        await set(plateRef, newCar.licensePlate);
      }

      // Add car to user's owned cars
      const ownedCarsRef = ref(database, `user_settings/${currentUser.uid}/ownedCars`);
      const ownedCarsSnapshot = await get(ownedCarsRef);
      const ownedCars = ownedCarsSnapshot.val() || {};
      await set(ownedCarsRef, {
        ...ownedCars,
        [Object.keys(ownedCars).length]: newCarRef.key
      });

      setNewCar({
        name: '',
        brand: '',
        model: '',
        year: '',
        licensePlate: ''
      });
    } catch (error) {
      console.error('Error creating new car:', error);
    }
  };

  const handleDeleteCar = async (carId: string) => {
    if (!currentUser || !window.confirm('Möchten Sie dieses Fahrzeug wirklich löschen?')) return;

    try {
      // First check if other users own this car
      const allUsersRef = ref(database, 'user_settings');
      const allUsersSnapshot = await get(allUsersRef);
      const allUsers = allUsersSnapshot.val() || {};
      
      let otherOwnersExist = false;
      for (const userId in allUsers) {
        if (userId === currentUser.uid) continue;
        const userOwnedCars = allUsers[userId]?.ownedCars || {};
        if (Object.values(userOwnedCars).includes(carId)) {
          otherOwnersExist = true;
          break;
        }
      }

      // Remove car from user's owned cars
      const ownedCarsRef = ref(database, `user_settings/${currentUser.uid}/ownedCars`);
      const ownedCarsSnapshot = await get(ownedCarsRef);
      const ownedCars = ownedCarsSnapshot.val() || {};
      const updatedOwnedCars = Object.entries(ownedCars)
        .filter(([_, value]) => value !== carId)
        .reduce((acc, [_, value], index) => ({ ...acc, [index]: value }), {});
      await set(ownedCarsRef, updatedOwnedCars);

      // Only delete the car from global collection if no other users own it
      if (!otherOwnersExist) {
        const carRef = ref(database, `cars/${carId}`);
        await remove(carRef);
      }
    } catch (error) {
      console.error('Error deleting car:', error);
    }
  };

  const handleToggleActive = async (carId: string, currentValue: boolean) => {
    try {
      const carRef = ref(database, `cars/${carId}`);
      const carSnapshot = await get(carRef);
      const carData = carSnapshot.val();
      await set(carRef, {
        ...carData,
        is_active: !currentValue
      });
    } catch (error) {
      console.error('Error toggling car active state:', error);
    }
  };

  const handleEdit = (carId: string) => {
    setEditingCars(prev => ({
      ...prev,
      [carId]: {
        ...prev[carId],
        isEditing: true
      }
    }));
  };

  const handleCancelEdit = (carId: string) => {
    setEditingCars(prev => ({
      ...prev,
      [carId]: {
        ...prev[carId],
        isEditing: false
      }
    }));
  };

  const handleSaveEdit = async (carId: string) => {
    try {
      const editedCar = editingCars[carId];
      const { isEditing, id, ...carData } = editedCar;

      // Update car data
      const carRef = ref(database, `cars/${carId}`);
      await set(carRef, {
        name: carData.name,
        brand: carData.brand,
        model: carData.model,
        year: carData.year,
        is_active: carData.is_active
      });

      // Update license plate separately
      const plateRef = ref(database, `cars/${carId}/plate`);
      await set(plateRef, carData.plate);

      setEditingCars(prev => ({
        ...prev,
        [carId]: {
          ...prev[carId],
          isEditing: false
        }
      }));
    } catch (error) {
      console.error('Error saving car:', error);
    }
  };

  const handleInputChange = (carId: string, field: keyof EditableCar, value: string | boolean) => {
    setEditingCars(prev => ({
      ...prev,
      [carId]: {
        ...prev[carId],
        [field]: value
      }
    }));
  };

  const ownedCars: EditableCar[] = ownedCarIds
    .map(id => ({ 
      ...editingCars[id],
      id,
      plate: carPlates[id] || ''
    }))
    .filter((car): car is EditableCar => Boolean(car.name));

  // Get paginated cars
  const indexOfLastCar = currentPage * itemsPerPage;
  const indexOfFirstCar = indexOfLastCar - itemsPerPage;
  const currentCars = ownedCars.slice(indexOfFirstCar, indexOfLastCar);
  const totalPages = Math.ceil(ownedCars.length / itemsPerPage);

  // Generate pagination items
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item 
        key={number} 
        active={number === currentPage}
        onClick={() => setCurrentPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  return (
    <Container>
      <h2 className="mb-4">Moscht Fahrzeuge</h2>
      
      {/* Navigation Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht" 
            active={location.pathname === '/dashboard/moscht'}
          >
            Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/tankungen" 
            active={location.pathname === '/dashboard/moscht/tankungen'}
          >
            Tankungen
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/events" 
            active={location.pathname === '/dashboard/moscht/events'}
          >
            Events
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/logs" 
            active={location.pathname === '/dashboard/moscht/logs'}
          >
            Logs
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/cars" 
            active={location.pathname === '/dashboard/moscht/cars'}
          >
            Fahrzeuge
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/moscht/settings" 
            active={location.pathname === '/dashboard/moscht/settings'}
          >
            Konfiguration
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      <Card className="mb-4">
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>Neues Fahrzeug</Card.Title>
          <Form onSubmit={handleSubmit} className="mt-3">
            <Form.Group className="mb-3">
              <Form.Label>Name*</Form.Label>
              <Form.Control
                type="text"
                value={newCar.name}
                onChange={(e) => setNewCar({ ...newCar, name: e.target.value })}
                placeholder="z.B. Mein Auto"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Marke</Form.Label>
              <Form.Control
                type="text"
                value={newCar.brand}
                onChange={(e) => setNewCar({ ...newCar, brand: e.target.value })}
                placeholder="z.B. VW"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Modell</Form.Label>
              <Form.Control
                type="text"
                value={newCar.model}
                onChange={(e) => setNewCar({ ...newCar, model: e.target.value })}
                placeholder="z.B. Golf"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Baujahr</Form.Label>
              <Form.Control
                type="text"
                value={newCar.year}
                onChange={(e) => setNewCar({ ...newCar, year: e.target.value })}
                placeholder="z.B. 2020"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kennzeichen</Form.Label>
              <Form.Control
                type="text"
                value={newCar.licensePlate}
                onChange={(e) => setNewCar({ ...newCar, licensePlate: e.target.value })}
                placeholder="z.B. BE 123456"
              />
            </Form.Group>

            <Button type="submit" variant="primary">
              Speichern
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <Card.Title style={{ color: '#1780a6' }}>Meine Fahrzeuge</Card.Title>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => setShowIds(!showIds)}
              className="mb-2"
            >
              {showIds ? 'IDs ausblenden' : 'IDs anzeigen'}
            </Button>
          </div>
          {ownedCars.length > 0 ? (
            <>
              <Table striped bordered hover className="mt-3">
                <thead>
                  <tr>
                    {showIds && <th>ID</th>}
                    <th>Name</th>
                    <th>Marke</th>
                    <th>Modell</th>
                    <th>Jahr</th>
                    <th>Kennzeichen</th>
                    <th>Aktiv</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {currentCars.map(car => (
                    <tr key={car.id}>
                      {showIds && <td>{car.id}</td>}
                      <td>
                        {car.isEditing ? (
                          <Form.Control
                            type="text"
                            value={car.name}
                            onChange={(e) => handleInputChange(car.id, 'name', e.target.value)}
                            required
                          />
                        ) : (
                          car.name
                        )}
                      </td>
                      <td>
                        {car.isEditing ? (
                          <Form.Control
                            type="text"
                            value={car.brand || ''}
                            onChange={(e) => handleInputChange(car.id, 'brand', e.target.value)}
                          />
                        ) : (
                          car.brand || '-'
                        )}
                      </td>
                      <td>
                        {car.isEditing ? (
                          <Form.Control
                            type="text"
                            value={car.model || ''}
                            onChange={(e) => handleInputChange(car.id, 'model', e.target.value)}
                          />
                        ) : (
                          car.model || '-'
                        )}
                      </td>
                      <td>
                        {car.isEditing ? (
                          <Form.Control
                            type="text"
                            value={car.year || ''}
                            onChange={(e) => handleInputChange(car.id, 'year', e.target.value)}
                          />
                        ) : (
                          car.year || '-'
                        )}
                      </td>
                      <td>
                        {car.isEditing ? (
                          <Form.Control
                            type="text"
                            value={car.plate || ''}
                            onChange={(e) => handleInputChange(car.id, 'plate', e.target.value)}
                          />
                        ) : (
                          car.plate || '-'
                        )}
                      </td>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={car.is_active}
                          onChange={() => handleToggleActive(car.id, car.is_active)}
                        />
                      </td>
                      <td>
                        {car.isEditing ? (
                          <>
                            <Button 
                              variant="success" 
                              size="sm"
                              className="me-2"
                              onClick={() => handleSaveEdit(car.id)}
                            >
                              Speichern
                            </Button>
                            <Button 
                              variant="secondary" 
                              size="sm"
                              onClick={() => handleCancelEdit(car.id)}
                            >
                              Abbrechen
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button 
                              variant="primary" 
                              size="sm"
                              className="me-2"
                              onClick={() => handleEdit(car.id)}
                            >
                              Bearbeiten
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleDeleteCar(car.id)}
                            >
                              Löschen
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-center mt-3">
                <Pagination>
                  <Pagination.First 
                    onClick={() => setCurrentPage(1)} 
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev 
                    onClick={() => setCurrentPage(curr => Math.max(curr - 1, 1))}
                    disabled={currentPage === 1}
                  />
                  {paginationItems}
                  <Pagination.Next 
                    onClick={() => setCurrentPage(curr => Math.min(curr + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last 
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            </>
          ) : (
            <div className="text-muted text-center mt-3">
              Keine Fahrzeuge vorhanden.
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MoschtCars;
